/**
 * Persistent upload queue using OPFS.
 *
 * Manages a queue of uploads that need to be processed by the service worker.
 * The queue persists across page reloads and browser restarts.
 *
 * Note: Uses a mutex to serialize operations because OPFS doesn't handle
 * concurrent access well, especially after page reloads.
 */

import { z } from 'zod';

import { createDebugLogger } from '@videa/utils';

const debug = createDebugLogger('UploadQueue');

// Helper for optional nullable string fields (accepts undefined, null, or string)
const optionalString = z.string().nullable().optional();

const uploadMetadataSchema = z.object({
  appointmentId: optionalString,
  patientVideaId: optionalString,
  templateId: optionalString,
  promptId: optionalString,
  organizationId: optionalString,
  practiceId: optionalString,
  patientConsentRecordId: optionalString,
});

export type UploadMetadata = z.infer<typeof uploadMetadataSchema>;

const queuedUploadSchema = z.object({
  sessionId: z.string(),
  noteId: z.string().optional(),
  metadata: uploadMetadataSchema,
  status: z.enum(['pending', 'uploading', 'completed', 'failed']),
  attempts: z.number(),
  lastAttempt: z.number().optional(),
  createdAt: z.number(),
  error: z.string().optional(),
});

export type QueuedUpload = z.infer<typeof queuedUploadSchema>;

const QUEUE_DIR = 'videa-notes-upload-queue';
const QUEUE_FILE = 'queue.json';

/**
 * Manages a persistent queue of uploads using OPFS.
 */
export class UploadQueue {
  private rootDirHandle: FileSystemDirectoryHandle | null = null;
  private operationMutex: Promise<void> = Promise.resolve();

  /**
   * Acquire the mutex and run an operation.
   * This ensures only one OPFS operation runs at a time to prevent
   * concurrent access issues that cause stale handle errors.
   */
  private async withMutex<T>(operation: () => Promise<T>): Promise<T> {
    // Chain this operation after the current one
    const currentMutex = this.operationMutex;
    let resolve: () => void;
    this.operationMutex = new Promise<void>((r) => {
      resolve = r;
    });

    try {
      // Wait for previous operation to complete
      await currentMutex;
      // Run this operation
      return await operation();
    } finally {
      // Release mutex for next operation
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- resolve is always assigned synchronously by the Promise constructor above
      resolve!();
    }
  }

  /**
   * Clear the cached directory handle.
   * Call this if operations are failing due to stale handles.
   */
  clearCache(): void {
    this.rootDirHandle = null;
  }

  /**
   * Check if an error is a stale handle error that can be retried.
   */
  private isStaleHandleError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return (
        error.name === 'NoModificationAllowedError' ||
        error.name === 'NotFoundError' ||
        error.name === 'InvalidStateError'
      );
    }
    return false;
  }

  /**
   * Get or create the queue directory.
   * Always fetches fresh handle to avoid stale reference issues.
   */
  private async getQueueDir(): Promise<FileSystemDirectoryHandle> {
    // Always get fresh handle to avoid stale reference issues
    const opfsRoot = await navigator.storage.getDirectory();
    this.rootDirHandle = await opfsRoot.getDirectoryHandle(QUEUE_DIR, {
      create: true,
    });
    return this.rootDirHandle;
  }

  /**
   * Read the current queue from OPFS.
   * Handles stale handle errors by retrying with fresh handles.
   */
  private async readQueue(isRetry = false): Promise<QueuedUpload[]> {
    try {
      const dir = await this.getQueueDir();
      const fileHandle = await dir.getFileHandle(QUEUE_FILE);
      const file = await fileHandle.getFile();
      const text = await file.text();
      const parsed = queuedUploadSchema.array().safeParse(JSON.parse(text));
      if (parsed.success) {
        return parsed.data;
      }
      return [];
    } catch (error) {
      // Check for stale handle errors that we should retry
      if (!isRetry && this.isStaleHandleError(error)) {
        // eslint-disable-next-line no-console
        debug.warn('Upload queue stale handle detected, retrying read...');
        this.clearCache();
        return this.readQueue(true);
      }
      // Queue file doesn't exist yet - expected on first use
      // eslint-disable-next-line no-console
      console.debug('Upload queue read failed (may not exist yet):', error);
      return [];
    }
  }

  /**
   * Write the queue to OPFS.
   * Handles stale handle errors by retrying with fresh handles.
   * Ensures writable stream is properly closed even on error.
   */
  private async writeQueue(
    queue: QueuedUpload[],
    isRetry = false,
  ): Promise<void> {
    let writable: FileSystemWritableFileStream | null = null;
    try {
      const dir = await this.getQueueDir();
      const fileHandle = await dir.getFileHandle(QUEUE_FILE, { create: true });
      writable = await fileHandle.createWritable();
      await writable.write(JSON.stringify(queue, null, 2));
      await writable.close();
      writable = null; // Mark as successfully closed
    } catch (error) {
      // Ensure stream is closed/aborted even on error
      if (writable) {
        try {
          await writable.abort();
        } catch {
          // Ignore abort errors
        }
      }
      // Check for stale handle errors that we should retry
      if (!isRetry && this.isStaleHandleError(error)) {
        // eslint-disable-next-line no-console
        debug.warn('Upload queue stale handle detected, retrying write...');
        this.clearCache();
        return this.writeQueue(queue, true);
      }
      throw error;
    }
  }

  /**
   * Add an upload to the queue.
   * Uses mutex to prevent concurrent access issues.
   */
  async enqueue(sessionId: string, metadata: UploadMetadata): Promise<void> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();

      // Check if already in queue
      const existing = queue.find((u) => u.sessionId === sessionId);
      if (existing) {
        return;
      }

      const upload: QueuedUpload = {
        sessionId,
        metadata,
        status: 'pending',
        attempts: 0,
        createdAt: Date.now(),
      };

      queue.push(upload);
      await this.writeQueue(queue);
    });
  }

  /**
   * Remove an upload from the queue.
   * Uses mutex to prevent concurrent access issues.
   */
  async dequeue(sessionId: string): Promise<void> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      const filtered = queue.filter((u) => u.sessionId !== sessionId);
      await this.writeQueue(filtered);
    });
  }

  /**
   * Get all queued uploads.
   * Uses mutex to prevent concurrent access issues.
   */
  async getQueue(): Promise<QueuedUpload[]> {
    return this.withMutex(() => this.readQueue());
  }

  /**
   * Get pending uploads (not yet started or failed and can retry).
   * Uses mutex to prevent concurrent access issues.
   */
  async getPendingUploads(maxRetries = 3): Promise<QueuedUpload[]> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      return queue.filter(
        (u) =>
          u.status === 'pending' ||
          (u.status === 'failed' && u.attempts < maxRetries),
      );
    });
  }

  /**
   * Get a specific upload by session ID.
   * Uses mutex to prevent concurrent access issues.
   */
  async getUpload(sessionId: string): Promise<QueuedUpload | undefined> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      return queue.find((u) => u.sessionId === sessionId);
    });
  }

  /**
   * Update the status of an upload.
   * Uses mutex to prevent concurrent access issues.
   */
  async updateStatus(
    sessionId: string,
    status: QueuedUpload['status'],
    noteId?: string,
    error?: string,
  ): Promise<void> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      const upload = queue.find((u) => u.sessionId === sessionId);

      if (upload) {
        upload.status = status;
        if (noteId) {
          upload.noteId = noteId;
        }
        if (error) {
          upload.error = error;
        }
        if (status === 'uploading' || status === 'failed') {
          upload.lastAttempt = Date.now();
        }
        await this.writeQueue(queue);
      }
    });
  }

  /**
   * Mark an upload as completed.
   */
  async markCompleted(sessionId: string, noteId?: string): Promise<void> {
    await this.updateStatus(sessionId, 'completed', noteId);
  }

  /**
   * Mark an upload as failed and increment attempt counter.
   * Uses mutex to prevent concurrent access issues.
   */
  async markFailed(sessionId: string, error?: string): Promise<void> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      const upload = queue.find((u) => u.sessionId === sessionId);

      if (upload) {
        upload.status = 'failed';
        upload.attempts += 1;
        upload.lastAttempt = Date.now();
        if (error) {
          upload.error = error;
        }
        await this.writeQueue(queue);
      }
    });
  }

  /**
   * Mark an upload as in progress.
   */
  async markUploading(sessionId: string): Promise<void> {
    await this.updateStatus(sessionId, 'uploading');
  }

  /**
   * Clean up completed uploads older than maxAgeMs.
   * Uses mutex to prevent concurrent access issues.
   */
  async cleanupCompleted(maxAgeMs: number): Promise<void> {
    return this.withMutex(async () => {
      const queue = await this.readQueue();
      const now = Date.now();

      const filtered = queue.filter(
        (u) => u.status !== 'completed' || now - u.createdAt < maxAgeMs,
      );

      if (filtered.length !== queue.length) {
        await this.writeQueue(filtered);
      }
    });
  }

  /**
   * Check if OPFS is available.
   */
  static isAvailable(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'getDirectory' in navigator.storage
    );
  }
}

// Default singleton instance
export const uploadQueue = new UploadQueue();
